/**
 * MasterDataCertificationFormDialog — Create/Edit `certification` theo Access
 * `frmMdQryCertificationAddver2`.
 *
 * Premium KHÔNG lưu ở bảng certification mà là LỊCH SỬ nhiều bản ghi trong
 * certification_change (mỗi bản = date_change + premium). Dialog gồm:
 *   - Certification (PK text, disable khi edit / sau khi đã tạo header)
 *   - Bảng lịch sử premium (Changed Date, Premium Value) — read từ API
 *   - Ô nhập Date + Premium + nút Save Premium / Delete Premium
 * SI/SD ẩn như Access (gửi null). Nút Save (title) = tạo/cập nhật header + đóng.
 *
 * UI theo /convert-form: Save/Cancel ở DialogTitle (top-right, dialogHeaderBtnSx),
 * DialogContent compact, field khóa dùng roSx, number type=text format-on-blur,
 * date DatePicker DD-MMM-YYYY. Enhancement (user-request "wow"): avatar huy hiệu +
 * stat cards + badge LATEST + highlight dòng chọn — chỉ trình bày, không đổi field.
 */
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useAddMdCertificationPremiumChangeMutation,
  useCreateMdCertificationMutation,
  useDeleteMdCertificationPremiumChangeMutation,
  useListMdCertificationPremiumChangesQuery,
  useUpdateMdCertificationMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataCertification } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataCertification | null
  onClose: () => void
  onSaved?: () => void
}

// Access Format='Standard' → #,##0.00. type=text để giữ dấu phẩy; format on blur.
const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const formatDate = (v: unknown): string => {
  if (!v) return ''
  const d = dayjs(String(v))
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}
const dayKey = (v: unknown): string => {
  const d = dayjs(String(v))
  return d.isValid() ? d.format('YYYY-MM-DD') : ''
}

export default function MasterDataCertificationFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCert, { isLoading: creating }] = useCreateMdCertificationMutation()
  const [updateCert, { isLoading: updating }] = useUpdateMdCertificationMutation()
  const [addPremium, { isLoading: addingPremium }] = useAddMdCertificationPremiumChangeMutation()
  const [deletePremium, { isLoading: deletingPremium }] = useDeleteMdCertificationPremiumChangeMutation()

  // PK certification hiện hành + đã tạo header trong DB chưa (New mode).
  const [code, setCode] = useState('')
  const [headerCreated, setHeaderCreated] = useState(false)
  // Ô nhập 1 bản ghi premium.
  const [dateChange, setDateChange] = useState('') // YYYY-MM-DD
  const [premiumInput, setPremiumInput] = useState('')
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setDateChange('')
    setPremiumInput('')
    if (isEdit && initialValue) {
      setCode(initialValue.certification ?? '')
      setHeaderCreated(true)
    } else {
      setCode('')
      setHeaderCreated(false)
    }
  }, [open, isEdit, initialValue])

  // Header đã tồn tại trong DB (edit từ đầu, hoặc create đã bấm tạo) → query lịch sử.
  const effectiveCode = headerCreated ? code.trim() : ''
  const { data: changesData, isFetching: loadingChanges } = useListMdCertificationPremiumChangesQuery(
    effectiveCode,
    { skip: !effectiveCode },
  )
  const changes = useMemo(() => changesData?.items ?? [], [changesData])

  // Bản ghi premium mới nhất (theo ngày) → dùng cho stat + badge LATEST.
  const latest = useMemo(() => {
    let best: (typeof changes)[number] | null = null
    for (const r of changes) {
      if (!r.changed_date) continue
      if (!best || dayjs(String(r.changed_date)).isAfter(dayjs(String(best.changed_date)))) best = r
    }
    return best
  }, [changes])

  const codeEmpty = !code.trim()
  const busy = creating || updating || addingPremium || deletingPremium
  const codeLocked = isEdit || headerCreated

  const handleClose = () => {
    if (busy) return
    onClose()
  }

  /** Đảm bảo header certification tồn tại trong DB (tạo nếu New chưa tạo). */
  const ensureHeader = async (): Promise<string | null> => {
    const c = code.trim()
    if (!c) {
      setShowErrors(true)
      return null
    }
    if (!headerCreated) {
      await createCert({ certification: c }).unwrap()
      setHeaderCreated(true)
    }
    return c
  }

  const handleSavePremium = async () => {
    if (codeEmpty || !dateChange) {
      setShowErrors(true)
      return
    }
    try {
      const c = await ensureHeader()
      if (!c) return
      await addPremium({
        certification: c,
        body: { date_change: dateChange, premium: parseNumeric(premiumInput), si: null, sd: null },
      }).unwrap()
      snack.success('Premium added')
      setDateChange('')
      setPremiumInput('')
      onSaved?.()
    } catch (e) {
      snack.error(getErrorMessage(e, 'Failed to add premium'))
    }
  }

  const handleDeletePremium = async () => {
    const c = code.trim()
    if (!c || !dateChange) {
      snack.warning('Please choose a premium record to delete!')
      return
    }
    try {
      await deletePremium({ certification: c, dateChange }).unwrap()
      snack.success('Premium deleted')
      setDateChange('')
      setPremiumInput('')
      onSaved?.()
    } catch (e) {
      snack.error(getErrorMessage(e, 'Failed to delete premium'))
    }
  }

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      const c = await ensureHeader()
      if (!c) return
      if (isEdit) {
        // Access ver2 btnSave: cập nhật audit của header.
        await updateCert({ code: c, body: {} }).unwrap()
      }
      snack.success(isEdit ? 'Certification updated' : 'Certification saved')
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  const selectRow = (changedDate: string | null, premium: number | null | undefined) => {
    if (!changedDate) return
    setDateChange(dayKey(changedDate))
    setPremiumInput(formatStandard(premium ?? ''))
  }

  const selectedKey = dateChange
  const editingExisting = !!selectedKey && changes.some((r) => dayKey(r.changed_date) === selectedKey)

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (busy) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Avatar
          variant="rounded"
          sx={{ width: 30, height: 30, bgcolor: 'primary.main', color: 'primary.contrastText' }}
        >
          <WorkspacePremiumRoundedIcon sx={{ fontSize: '1.1rem' }} />
        </Avatar>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Certification' : 'New Certification'}
          {code ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={codeEmpty || busy}
            sx={dialogHeaderBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={busy} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ── Certification code ────────────────────────────────────── */}
        <Box sx={{ mt: 0.5 }}>
          <TextField
            label="Certification"
            size="small"
            fullWidth
            required
            disabled={codeLocked}
            error={showErrors && codeEmpty}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: codeLocked ? -1 : 1 }}
            helperText={codeLocked ? 'Certification code cannot be changed after creation.' : 'Enter the code, then add premium records below.'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {codeLocked ? <LockOutlinedIcon fontSize="small" color="disabled" /> : <BadgeOutlinedIcon fontSize="small" color="action" />}
                </InputAdornment>
              ),
            }}
            sx={codeLocked ? roSx : undefined}
          />
        </Box>

        {/* ── Stat cards (enhancement, hiện khi đã có lịch sử) ──────── */}
        <Fade in={!!effectiveCode && changes.length > 0} unmountOnExit>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <StatCard
              icon={<PaidOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Latest premium"
              value={latest ? formatStandard(latest.premium) || '—' : '—'}
              accent="secondary"
              emphasize
            />
            <StatCard
              icon={<HistoryRoundedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Records"
              value={String(changes.length)}
              accent="primary"
            />
            <StatCard
              icon={<EventOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
              label="Last updated"
              value={latest ? formatDate(latest.changed_date) || '—' : '—'}
              accent="info"
            />
          </Stack>
        </Fade>

        {/* ── Premium history (Access frmMdQryCertificationChange subform) ── */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.75, mb: 0.5 }}>
          <HistoryRoundedIcon sx={{ fontSize: '0.95rem', color: 'primary.main' }} />
          <Typography sx={sectionHeaderSx}>Premium history</Typography>
          {effectiveCode && changes.length > 0 ? (
            <Chip size="small" label={changes.length} color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }} />
          ) : null}
        </Stack>
        <Paper variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 220, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Changed date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Premium value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!effectiveCode ? (
                <EmptyRow text="Save the certification first to manage premium history." />
              ) : loadingChanges ? (
                <TableRow><TableCell colSpan={2} align="center" sx={{ py: 2.5 }}><CircularProgress size={16} /></TableCell></TableRow>
              ) : changes.length === 0 ? (
                <EmptyRow text="No premium records yet — add the first one below." />
              ) : (
                changes.map((r, i) => {
                  const key = dayKey(r.changed_date)
                  const selected = !!selectedKey && key === selectedKey
                  const isLatest = !!latest && key === dayKey(latest.changed_date)
                  return (
                    <TableRow
                      key={`${r.changed_date}-${i}`}
                      hover
                      selected={selected}
                      sx={{
                        cursor: 'pointer',
                        '& > td': { borderLeft: selected ? (t) => `3px solid ${t.palette.secondary.main}` : '3px solid transparent' },
                        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: (t) => `${t.palette.secondary.light}55` },
                      }}
                      onClick={() => selectRow(r.changed_date, r.premium)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <span>{formatDate(r.changed_date)}</span>
                          {isLatest ? (
                            <Chip
                              size="small"
                              label="LATEST"
                              sx={{ height: 15, fontSize: '0.56rem', fontWeight: 800, letterSpacing: 0.4, color: (t) => t.palette.success.dark, bgcolor: (t) => `${t.palette.success.light}44` }}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: isLatest ? 700 : 500 }}>
                        {formatStandard(r.premium)}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* ── Add / update premium (ô nhập của subform) ─────────────── */}
        <Typography sx={{ ...sectionHeaderSx, mt: 1.75, mb: 0.5 }}>
          {editingExisting ? 'Update premium for selected date' : 'Add a premium record'}
        </Typography>
        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fffaf3'),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              value={dateChange ? dayjs(dateChange) : null}
              onChange={(d) => setDateChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErrors && !dateChange,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 2 },
                },
              }}
            />
            <TextField
              label="Premium"
              size="small"
              fullWidth
              value={premiumInput}
              onChange={(e) => setPremiumInput(e.target.value)}
              onBlur={() => setPremiumInput(formatStandard(premiumInput))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3, inputMode: 'decimal', style: { textAlign: 'right' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PaidOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              sx={numSx}
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Tooltip title={editingExisting ? 'Remove the selected premium record' : 'Pick a row above to delete it'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={() => void handleDeletePremium()}
                  disabled={busy || !dateChange || !effectiveCode}
                >
                  Delete premium
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => void handleSavePremium()}
              disabled={busy || codeEmpty || !dateChange}
            >
              {editingExisting ? 'Update premium' : 'Add premium'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

/** Stat card nhỏ trong hàng thống kê (enhancement, chỉ display). */
function StatCard({
  icon,
  label,
  value,
  accent,
  emphasize,
}: {
  icon: ReactNode
  label: string
  value: string
  accent: 'primary' | 'secondary' | 'info'
  emphasize?: boolean
}) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, px: 1.25, py: 0.75, borderRadius: 1.5, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: `${accent}.main`, mb: 0.25 }}>
        {icon}
        <Typography noWrap sx={{ fontSize: '0.6rem', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      <Typography
        noWrap
        sx={{
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          fontSize: emphasize ? '1.05rem' : '0.9rem',
          color: emphasize ? `${accent}.dark` : 'text.primary',
          lineHeight: 1.25,
        }}
      >
        {value}
      </Typography>
    </Paper>
  )
}

/** Hàng trống trung tâm có chữ mờ. */
function EmptyRow({ text }: { text: string }) {
  return (
    <TableRow>
      <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 2.5, fontSize: '0.78rem' }}>
        {text}
      </TableCell>
    </TableRow>
  )
}
